import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Container,
  Divider,
  Chip,
  Grid
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import api from '../utils/api';
import { formatDateOnly } from '../utils/dateFormat';
import {
  fetchDocumentPdfBlobUrl,
  openDocumentPdf,
  revokeDocumentPdfBlobUrl,
} from '../utils/documentFile';

interface Document {
  id: number;
  title: string;
  description: string;
  file: string;
  version: string;
  revision_date: string;
  category?: {
    id: number;
    name: string;
    color: string;
  };
  created_by: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  };
  file_size: number;
}

interface Assignment {
  id: number;
  assignment_type: string;
  content_title: string;
  description: string;
  status: string;
  due_date: string;
  is_overdue: boolean;
  completion_count: number;
  is_completed: boolean;
  assigned_by: {
    email: string;
    first_name: string;
    last_name: string;
  };
  assignment_completions: Array<{
    id: number;
    status: string;
    started_at: string;
    completed_at: string;
    score: number;
    attempts: number;
  }>;
  object_id: string;
}

interface DocumentAcknowledgment {
  id: number;
  status: string;
  acknowledged_at?: string;
  notes?: string;
  document?: number;
}

const DocumentReview: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const [searchParams] = useSearchParams();
  const assignmentIdParam = searchParams.get('assignment');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [document, setDocument] = useState<Document | null>(null);
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [acknowledgment, setAcknowledgment] = useState<DocumentAcknowledgment | null>(null);
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    if (documentId) {
      fetchDocumentData();
    }
  }, [documentId]);

  useEffect(() => {
    if (!document?.id) {
      return;
    }

    let active = true;
    let blobUrl: string | null = null;

    const loadPdf = async () => {
      setPdfLoading(true);
      setPdfError(null);
      setPdfBlobUrl((prev) => {
        revokeDocumentPdfBlobUrl(prev);
        return null;
      });

      try {
        blobUrl = await fetchDocumentPdfBlobUrl(document.id);
        if (active) {
          setPdfBlobUrl(blobUrl);
        } else if (blobUrl) {
          revokeDocumentPdfBlobUrl(blobUrl);
        }
      } catch (err) {
        console.error('Error loading document PDF:', err);
        if (active) {
          setPdfError(
            'Unable to load the document PDF. The file may be missing from the server and need to be re-uploaded.'
          );
        }
      } finally {
        if (active) {
          setPdfLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      active = false;
      if (blobUrl) {
        revokeDocumentPdfBlobUrl(blobUrl);
      }
    };
  }, [document?.id]);

  const fetchDocumentData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch document details
      const docResponse = await api.get(`/documents/${documentId}/`);
      console.log('Document API response:', docResponse.data);
      console.log('Document file field:', docResponse.data.file);
      setDocument(docResponse.data);

      // Fetch assignment details for current user (if any)
      try {
        let foundAssignment = null;
        
        if (assignmentIdParam) {
          // Use the assignment ID from URL params directly
          console.log('Using assignment ID from URL params:', assignmentIdParam);
          const assignmentResponse = await api.get(`/assignments/${assignmentIdParam}/`);
          foundAssignment = assignmentResponse.data;
          console.log('Found assignment from URL params:', foundAssignment);
        } else {
          // Fall back to searching for assignment by document ID
          console.log('Searching for assignment by document ID:', documentId);
          const assignmentResponse = await api.get(`/assignments/?assignment_type=document&object_id=${documentId}`);
          const userAssignments = assignmentResponse.data.results || assignmentResponse.data;
          if (userAssignments.length > 0) {
            foundAssignment = userAssignments[0]; // Take first assignment for now
            console.log('Found assignment by document search:', foundAssignment);
          } else {
            console.log('No assignment found for this document');
          }
        }
        
        if (foundAssignment) {
          setAssignment(foundAssignment);
          
          // Fetch canonical acknowledgment status first, then fall back to AssignmentCompletion.
          try {
            if (user) {
              const ackResponse = await api.get(`/document-acknowledgments/?document=${documentId}&user=${user.id}`);
              const acknowledgments = ackResponse.data.results || ackResponse.data || [];
              const acknowledgedRecord = acknowledgments.find((ack: any) => ack.status === 'acknowledged');
              if (acknowledgedRecord) {
                setAcknowledgment(acknowledgedRecord);
                return;
              }
            }

            const completionResponse = await api.get(`/assignment-completions/?assignment=${foundAssignment.id}`);
            const userCompletions = completionResponse.data.results || [];
            console.log('Fetched assignment completions for assignment:', foundAssignment.id, 'user:', user?.id, 'completions:', userCompletions);
            
            const completedAssignment = userCompletions.find(
              (comp: any) => comp.status === 'completed' && (!user || comp.assignee?.id === user.id)
            );
            if (completedAssignment && completedAssignment.completion_data?.acknowledged_at) {
              // Create acknowledgment object from completion data
              setAcknowledgment({
                id: completedAssignment.id,
                status: 'acknowledged',
                acknowledged_at: completedAssignment.completion_data.acknowledged_at,
                document: documentId ? parseInt(documentId) : undefined,
                notes: ''
              });
              console.log('Set acknowledgment from assignment completion:', completedAssignment);
            } else {
              console.log('No acknowledgment found for current user in assignment completion');
            }
          } catch (error) {
            console.log('Error fetching acknowledgment for this document:', error);
          }
        }
      } catch (error) {
        console.log('Error fetching assignment for this document:', error);
      }

    } catch (error: any) {
      console.error('Error fetching document data:', error);
      setError('Failed to load document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    if (!document || !assignment) return;

    try {
      setAcknowledging(true);
      setError(null);

      const acknowledgedAt = new Date().toISOString();
      const acknowledgmentResponse = await api.post('/document-acknowledgments/', {
        document: document.id
      });

      // Keep AssignmentCompletion aligned for the assignments UI.
      const assignmentCompletionData = {
        status: 'completed',
        notes: 'Document acknowledged successfully',
        completion_data: {
          acknowledged_at: acknowledgedAt,
          document_id: document.id,
          document_title: document.title,
          times_viewed: 1,
          view_duration: 0
        }
      };

      // Check if AssignmentCompletion already exists
      const existingResponse = await api.get(`/assignment-completions/?assignment=${assignment.id}`);
      const existingCompletions = existingResponse.data.results || existingResponse.data || [];
      const existingCompletion = existingCompletions.find(
        (comp: any) => !user || comp.assignee?.id === user.id
      );
      
      if (existingCompletion) {
        await api.patch(`/assignment-completions/${existingCompletion.id}/`, assignmentCompletionData);
      } else {
        // Create new AssignmentCompletion 
        if (!user) {
          throw new Error('User not authenticated');
        }
        await api.post('/assignment-completions/', {
          assignment: assignment.id,
          assignee_id: user.id,
          ...assignmentCompletionData
        });
      }

      setAcknowledgment(acknowledgmentResponse.data);
      
      setSuccess('Document acknowledged successfully!');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (error: any) {
      console.error('Error acknowledging document:', error);
      setError('Failed to acknowledge document. Please try again.');
    } finally {
      setAcknowledging(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return formatDateOnly(dateString, '', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }, 'en-US');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !document) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Document not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/my-assignments')}
          variant="outlined"
        >
          Back to Assignments
        </Button>
      </Container>
    );
  }

  const isAcknowledged = acknowledgment?.status === 'acknowledged';
  const hasAssignment = assignment !== null;
  const showAcknowledgeButton = hasAssignment && !isAcknowledged;

  if (!document.file) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Document file not found or not accessible. Document ID: {documentId}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/my-assignments')}
          variant="outlined"
        >
          Back to Assignments
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/my-assignments')}
          variant="outlined"
          sx={{ mb: 2 }}
        >
          Back to Assignments
        </Button>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="h4" component="h1" gutterBottom>
          {document.title}
        </Typography>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Chip 
              icon={<AssignmentIcon />}
              label={document.version}
              color="primary"
              variant="outlined"
            />
          </Grid>
          {document.category && (
            <Grid item>
              <Chip 
                label={document.category.name}
                sx={{ 
                  backgroundColor: document.category.color,
                  color: 'white'
                }}
              />
            </Grid>
          )}
          {isAcknowledged && (
            <Grid item>
              <Chip 
                icon={<CheckCircleIcon />}
                label="Acknowledged"
                color="success"
              />
            </Grid>
          )}
        </Grid>

        {document.description && (
          <Typography variant="body1" color="text.secondary" paragraph>
            {document.description}
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary">
          Revision Date: {formatDate(document.revision_date)} • 
          Size: {formatFileSize(document.file_size)} • 
          Created by: {document.created_by.first_name} {document.created_by.last_name}
        </Typography>

        {assignment && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Assigned by: {assignment.assigned_by.first_name} {assignment.assigned_by.last_name}
            {assignment.due_date && (
              <> • Due: {formatDate(assignment.due_date)}</>
            )}
          </Typography>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* PDF Viewer Container */}
      <Paper elevation={1} sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ height: '700px', width: '100%' }}>
          {pdfError && (
            <Alert severity="error" sx={{ m: 2 }}>
              {pdfError}
            </Alert>
          )}
          {pdfLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          )}
          {!pdfLoading && pdfBlobUrl && (
            <div>
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', mb: 1, borderBottom: '1px solid #ddd' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => openDocumentPdf(document.id)}
                  sx={{ mr: 2 }}
                >
                  Open PDF in New Tab
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const link = window.document.createElement('a');
                    link.href = pdfBlobUrl;
                    link.download = `${document.title}.pdf`;
                    link.click();
                  }}
                >
                  Download PDF
                </Button>
              </Box>

              <iframe
                key={document.id}
                src={`${pdfBlobUrl}#navpanes=0&toolbar=1`}
                style={{
                  width: '100%',
                  height: '620px',
                  border: 'none',
                  borderRadius: '4px'
                }}
                title={`PDF Viewer - ${document.title}`}
              />
            </div>
          )}
        </Box>
      </Paper>

      {/* Acknowledgment Section */}
      {showAcknowledgeButton && (
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Document Acknowledgment
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            By clicking "I Acknowledge This Document", you confirm that you have read and understood the content of this document.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleAcknowledge}
            disabled={acknowledging}
            startIcon={acknowledging ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {acknowledging ? 'Acknowledging...' : 'I Acknowledge This Document'}
          </Button>
        </Paper>
      )}

      {/* Acknowledgment Status */}
      {isAcknowledged && acknowledgment && (
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center', backgroundColor: 'success.light', color: 'success.contrastText' }}>
          <CheckCircleIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Document Acknowledged
          </Typography>
          <Typography variant="body2">
            You acknowledged this document on {acknowledgment.acknowledged_at ? formatDate(acknowledgment.acknowledged_at) : 'Unknown date'}
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default DocumentReview;